import { useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import {
  HardHat, Save, Loader2, X, ShieldCheck, Shield, AlertTriangle, ShieldAlert,
  Pencil, Plus, Trash2, ChevronDown, ChevronRight, TrendingUp, ClipboardList,
} from "lucide-react";

const STATUS_STYLES = {
  PREFERRED:  { bg: "bg-[#064E2E]",        text: "text-white",    dot: "🟢", icon: ShieldCheck,  label: "Preferred",  ring: "ring-[#064E2E]/30" },
  APPROVED:   { bg: "bg-[#0F766E]",        text: "text-white",    dot: "🟡", icon: Shield,       label: "Approved",   ring: "ring-[#0F766E]/30" },
  MONITOR:    { bg: "bg-[#B88A2A]",        text: "text-white",    dot: "🟠", icon: AlertTriangle,label: "Monitor",    ring: "ring-[#B88A2A]/30" },
  RESTRICTED: { bg: "bg-[#B91C1C]",        text: "text-white",    dot: "🔴", icon: ShieldAlert,  label: "Restricted", ring: "ring-[#B91C1C]/30" },
  UNRATED:    { bg: "bg-[#475569]",        text: "text-white",    dot: "⚪", icon: Shield,       label: "Unrated",    ring: "ring-[#475569]/30" },
};

const ISSUE_TYPES = ["COMPLAINT", "QUALITY", "INVOICE_DISPUTE", "NO_SHOW", "SAFETY", "OTHER"];

/** Compact inline status pill — usable in tables / row headers. */
export function ContractorHealthBadge({ contractor, size = "md", showScore = true }) {
  if (!contractor) return null;
  const s = STATUS_STYLES[contractor.effective_status] || STATUS_STYLES.UNRATED;
  const Icon = s.icon;
  const dims = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium tracking-[0.06em] uppercase ${dims} ${s.bg} ${s.text}`}
      title={contractor.status_override ? `Manual override: ${contractor.status_override}${contractor.override_reason ? ` — ${contractor.override_reason}` : ""}` : `Auto: ${contractor.derived_status}`}
      data-testid={`contractor-badge-${contractor.id}`}
    >
      <Icon size={size === "sm" ? 10 : 12}/>
      {s.label}
      {showScore && contractor.health_score != null && (
        <span className="opacity-80 tabular-nums">· {contractor.health_score}</span>
      )}
      {contractor.status_override && <span className="opacity-70 text-[9px]">M</span>}
    </span>
  );
}

/** Full expandable health card — usable inside IntelligencePanel.
 *  Loads contractor via /admin/contractors/by-name/{name} unless a `contractor` prop is passed in.
 */
export default function ContractorHealthCard({ contractor: initial, onUpdated }) {
  const [contractor, setContractor] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [addingIssue, setAddingIssue] = useState(false);
  const [issueDraft, setIssueDraft] = useState({ type: "COMPLAINT", note: "" });
  const [openIssues, setOpenIssues] = useState(false);

  if (!contractor) return null;
  const s = STATUS_STYLES[contractor.effective_status] || STATUS_STYLES.UNRATED;
  const Icon = s.icon;
  const issues = contractor.issues || [];

  async function refresh() {
    try {
      const { data } = await adminApi.get(`/contractors/by-name/${encodeURIComponent(contractor.name)}`);
      setContractor(data);
      onUpdated?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not refresh contractor");
    }
  }

  async function setOverride(newStatus) {
    try {
      const { data } = await adminApi.put(`/contractors/${contractor.id}/status-override`, {
        status_override: newStatus,
        reason: newStatus ? (window.prompt(`Reason for ${newStatus} override (optional)`) || "") : "",
      });
      setContractor(data);
      onUpdated?.(data);
      toast.success(newStatus ? `Override → ${newStatus}` : "Override cleared");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  async function submitIssue(e) {
    e.preventDefault();
    if (!issueDraft.note.trim()) return;
    try {
      await adminApi.post(`/contractors/${contractor.id}/issue`, issueDraft);
      toast.success("Issue logged");
      setIssueDraft({ type: "COMPLAINT", note: "" });
      setAddingIssue(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  async function removeIssue(issueId) {
    if (!window.confirm("Remove this issue from the contractor's record?")) return;
    try {
      await adminApi.delete(`/contractors/${contractor.id}/issue/${issueId}`);
      toast.success("Issue removed");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="bg-white border border-[#E2DCD0] rounded-lg overflow-hidden" data-testid="contractor-health-card">
      {/* HEADER */}
      <div className={`px-4 py-3 ${s.bg} ${s.text} flex items-center justify-between gap-3`}>
        <div className="flex items-center gap-2.5 min-w-0">
          <Icon size={18} className="shrink-0"/>
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] uppercase opacity-80">{contractor.status_override ? "Manual override · was " + contractor.derived_status : "Auto-rated"}</p>
            <p className="font-medium leading-tight truncate">{s.label}{contractor.health_score != null && <span className="ml-2 opacity-80 tabular-nums">{contractor.health_score}/100</span>}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setEditing(true)}
            className={`inline-flex items-center gap-1 ${s.text} text-[11px] bg-white/15 hover:bg-white/25 px-2.5 py-1 rounded-full font-medium transition`}
            data-testid="contractor-edit-metrics"
          >
            <Pencil size={11}/> Metrics
          </button>
          <details className="relative">
            <summary className={`list-none cursor-pointer ${s.text} text-[11px] bg-white/15 hover:bg-white/25 px-2.5 py-1 rounded-full font-medium transition`}>Override ▾</summary>
            <div className="absolute right-0 top-full mt-1 bg-white text-[#071A33] rounded-lg shadow-lg ring-1 ring-black/5 py-1 min-w-[170px] z-10">
              {Object.entries(STATUS_STYLES).filter(([k]) => k !== "UNRATED").map(([k, st]) => (
                <button key={k} onClick={() => setOverride(k)} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-[#F7F4ED] flex items-center gap-1.5">
                  <st.icon size={11}/>{st.label}
                </button>
              ))}
              {contractor.status_override && (
                <button onClick={() => setOverride(null)} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-[#F7F4ED] text-[#475569] border-t border-[#E2DCD0]">
                  Clear override
                </button>
              )}
            </div>
          </details>
        </div>
      </div>

      {/* TRADE / CONTACT */}
      {(contractor.primary_trade || contractor.email || contractor.phone) && (
        <div className="px-4 py-2 bg-[#F7F4ED] text-xs text-[#475569] flex flex-wrap gap-x-4 gap-y-1">
          {contractor.primary_trade && <span className="text-[#071A33] font-medium">{contractor.primary_trade}</span>}
          {contractor.email && <span>{contractor.email}</span>}
          {contractor.phone && <span>{contractor.phone}</span>}
        </div>
      )}

      {/* STAT STRIP */}
      <div className="grid grid-cols-3 divide-x divide-[#E2DCD0] border-b border-[#E2DCD0]">
        <Stat label="Active jobs" value={contractor.active_jobs} highlight={contractor.active_jobs >= 5}/>
        <Stat label="Total cases" value={contractor.total_cases}/>
        <Stat label="Open issues" value={issues.length} highlight={issues.length >= 1}/>
      </div>

      {/* KPI BARS */}
      <div className="p-4 grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
        {contractor.kpis.map((k) => <KpiBar key={k.key} kpi={k}/>)}
      </div>

      {/* ISSUES */}
      <div className="border-t border-[#E2DCD0]">
        <button
          type="button"
          onClick={() => setOpenIssues((v) => !v)}
          className="w-full px-4 py-2.5 flex items-center justify-between text-left text-sm hover:bg-[#F7F4ED]"
          data-testid="contractor-issues-toggle"
        >
          <span className="flex items-center gap-2 text-[#071A33]">
            {openIssues ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
            <ClipboardList size={14} className="text-[#B88A2A]"/>
            Historical issues
            {issues.length > 0 && <span className="text-xs bg-[#B91C1C]/10 text-[#B91C1C] px-1.5 py-0.5 rounded">{issues.length}</span>}
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); setOpenIssues(true); setAddingIssue(true); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setOpenIssues(true); setAddingIssue(true); } }}
            className="text-xs text-[#0F766E] hover:underline inline-flex items-center gap-1 cursor-pointer"
            data-testid="contractor-add-issue"
          >
            <Plus size={11}/> Log issue
          </span>
        </button>
        {openIssues && (
          <div className="px-4 pb-3 space-y-2">
            {addingIssue && (
              <form onSubmit={submitIssue} className="bg-[#F7F4ED] border border-[#E2DCD0] rounded-lg p-3 space-y-2" data-testid="contractor-issue-form">
                <div className="flex gap-2">
                  <select className="field-input m-0 h-9 text-xs flex-none w-40" value={issueDraft.type} onChange={(e) => setIssueDraft((d) => ({ ...d, type: e.target.value }))}>
                    {ISSUE_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                  </select>
                  <input className="field-input m-0 h-9 text-xs flex-1" placeholder="What happened?" value={issueDraft.note} onChange={(e) => setIssueDraft((d) => ({ ...d, note: e.target.value }))} autoFocus data-testid="contractor-issue-note"/>
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setAddingIssue(false)} className="text-xs text-[#475569] hover:text-[#071A33]">Cancel</button>
                  <button type="submit" className="text-xs bg-[#064E2E] text-white px-3 py-1.5 rounded-full font-medium" data-testid="contractor-issue-submit">Save</button>
                </div>
              </form>
            )}
            {issues.length === 0 && !addingIssue && (
              <p className="text-xs text-[#475569] italic">No issues recorded.</p>
            )}
            {issues.map((it) => (
              <div key={it.id} className="text-xs flex items-start justify-between gap-2 py-1.5 border-b border-[#E2DCD0]/60 last:border-0">
                <div className="min-w-0">
                  <p className="text-[#071A33]">
                    <span className="bg-[#B91C1C]/10 text-[#B91C1C] px-1.5 py-0.5 rounded mr-1.5">{it.type.replace("_", " ")}</span>
                    {it.note}
                  </p>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">{new Date(it.logged_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}{it.case_id && ` · case ${it.case_id.slice(0, 8)}`}</p>
                </div>
                <button onClick={() => removeIssue(it.id)} className="text-[#94A3B8] hover:text-[#B91C1C] p-1"><Trash2 size={11}/></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <MetricsDialog
          contractor={contractor}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { setContractor(updated); setEditing(false); onUpdated?.(updated); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`px-3 py-2 text-center ${highlight ? "bg-[#B91C1C]/5" : ""}`}>
      <p className={`font-display text-xl tabular-nums leading-none ${highlight ? "text-[#B91C1C]" : "text-[#071A33]"}`}>{value ?? "—"}</p>
      <p className="text-[10px] tracking-[0.15em] uppercase text-[#475569] mt-1">{label}</p>
    </div>
  );
}

function KpiBar({ kpi }) {
  const v = kpi.value;
  const set = kpi.set;
  const colour = v == null ? "bg-[#E2DCD0]"
    : v >= 85 ? "bg-[#064E2E]"
    : v >= 70 ? "bg-[#0F766E]"
    : v >= 50 ? "bg-[#B88A2A]"
    : "bg-[#B91C1C]";
  return (
    <div className="text-xs">
      <div className="flex justify-between items-baseline">
        <span className="text-[#475569]">{kpi.label}</span>
        <span className={`tabular-nums font-medium ${set ? "text-[#071A33]" : "text-[#94A3B8]"}`}>{set ? Math.round(v) : "—"}</span>
      </div>
      <div className="mt-1 h-1.5 bg-[#F7F4ED] rounded-full overflow-hidden">
        <div className={`h-full ${colour} transition-all`} style={{ width: set ? `${v}%` : "4px" }}/>
      </div>
    </div>
  );
}

function MetricsDialog({ contractor, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const out = {
      primary_trade: contractor.primary_trade || "",
      email: contractor.email || "",
      phone: contractor.phone || "",
      notes: contractor.notes || "",
    };
    for (const k of contractor.kpis) {
      out[k.key] = k.value ?? "";
    }
    return out;
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // empty strings → null (don't update that metric)
      for (const k of contractor.kpis) {
        payload[k.key] = form[k.key] === "" ? null : Number(form[k.key]);
      }
      const { data } = await adminApi.put(`/contractors/${contractor.id}/metrics`, payload);
      toast.success("Metrics updated");
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 p-4 overflow-y-auto" data-testid="contractor-metrics-dialog">
      <form onSubmit={submit} className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E2DCD0] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HardHat size={20} className="text-[#064E2E]"/>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#B88A2A]">Contractor metrics</p>
              <h2 className="font-display text-xl text-[#071A33]">{contractor.name}</h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[#475569] hover:text-[#071A33]"><X size={20}/></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="field-label">Primary trade</label>
              <input className="field-input" value={form.primary_trade} onChange={(e) => update("primary_trade", e.target.value)} placeholder="e.g. Roofing"/>
            </div>
            <div>
              <label className="field-label">Email</label>
              <input className="field-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Phone</label>
              <input className="field-input" value={form.phone} onChange={(e) => update("phone", e.target.value)}/>
            </div>
          </div>
          <div className="bg-[#F7F4ED] rounded-xl p-4">
            <p className="text-xs tracking-[0.2em] uppercase text-[#B88A2A] mb-3">8 KPIs · 0–100 (higher = better, leave blank to skip)</p>
            <div className="grid sm:grid-cols-2 gap-x-5 gap-y-3">
              {contractor.kpis.map((k) => (
                <div key={k.key}>
                  <label className="flex items-baseline justify-between text-xs text-[#475569] mb-1">
                    <span>{k.label}</span>
                    <span className="tabular-nums font-medium text-[#071A33]">{form[k.key] === "" ? "—" : Math.round(Number(form[k.key]))}</span>
                  </label>
                  <input
                    type="range" min="0" max="100" step="1"
                    value={form[k.key] === "" ? 0 : form[k.key]}
                    onChange={(e) => update(k.key, e.target.value)}
                    className="w-full accent-[#064E2E]"
                    data-testid={`metric-${k.key}-range`}
                  />
                  {form[k.key] !== "" && (
                    <button type="button" onClick={() => update(k.key, "")} className="text-[10px] text-[#94A3B8] hover:text-[#475569] mt-0.5">Clear</button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="field-label">Notes</label>
            <textarea className="field-input min-h-[70px]" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Internal context not shown to customers"/>
          </div>
        </div>
        <div className="px-6 py-4 bg-[#F7F4ED] border-t border-[#E2DCD0] flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} className="btn-outline" disabled={saving}>Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="metrics-save-btn">
            {saving ? <><Loader2 className="animate-spin" size={14}/> Saving…</> : <><Save size={14}/> Save metrics</>}
          </button>
        </div>
      </form>
    </div>
  );
}
